import { useAppDispatch, useAppSelector } from "@/app/appStore";
import { InfoItem } from "@/components/info-item";
import {
  selectAnnotationData,
  selectIsEditable,
  setAnnotationData,
  setCurrentCamera,
  setSelectedEventById,
  addJourneyProximity,
  HandId,
  selectActiveJourneyProximities,
  setGondolaByName,
  setGondolaDropdownValueByName,
  setHighlightedTimelineEventId,
} from "@/entities/annotation";
import {
  Text,
  Flex,
  IconButton,
  TextField,
  Button,
  SegmentedControl,
} from "@radix-ui/themes";
import { TargetIcon } from "@radix-ui/react-icons";
import { hasIdenticalAnnotationTimestamp } from "../../lib/helpers";
import { useState } from "react";
import { displayUUID, formatAbsoluteCoordinates } from "@/shared";
import { useTrackedHotkeys } from "@/shared/hooks/useTrackedHotkeys";
import { AnnotationFormWrapper } from "../../ui/annotation-form-wrapper";
import { useTimelineData } from "../../hooks/useTimelineData";
import {
  ANNOTATION_TYPES,
  ANNOTATION_RECORD_STATUSES,
  HAND_IDS,
  HAND_SIDES,
} from "@/entities/annotation/model/consts";
import toast from "react-hot-toast";
import { useCalibration } from "../../lib/useCalibration";
import { usePlanogram } from "../../lib/usePlanogram";
import { useCameras } from "../../hooks/useCameras";
import { usePlayer } from "@tezzro/player";

const ProximityAnnotationForm = () => {
  const player = usePlayer();
  const annotationData = useAppSelector(selectAnnotationData);
  const dispatch = useAppDispatch();
  const { addTimelineJourneyProximityEvent } = useTimelineData();
  const isEditable = useAppSelector(selectIsEditable);
  const activeJourneyProximities = useAppSelector(
    selectActiveJourneyProximities
  );
  const [handId, setHandId] = useState<HandId>("0");
  const [note, setNote] = useState("");
  const { planogram } = usePlanogram();
  const { calibration } = useCalibration();
  const cameraData = useCameras();

  const handleApply = () => {
    if (
      !annotationData?.gondolaId ||
      !annotationData?.gondolaName ||
      !annotationData?.timestamp ||
      !annotationData?.pinCoordinates ||
      !planogram?.id ||
      !calibration?.id
    ) {
      return null;
    }

    if (
      hasIdenticalAnnotationTimestamp(
        activeJourneyProximities,
        annotationData.timestamp
      )
    ) {
      toast.error(
        "Same type annotations within a journey must have different timestamps"
      );
      return;
    }

    const customId = crypto.randomUUID();

    dispatch(
      addJourneyProximity({
        type: ANNOTATION_TYPES.PROXIMITY,
        status: ANNOTATION_RECORD_STATUSES.NEW,
        id: customId,
        orderId: annotationData.orderId,
        journeyId: annotationData.journeyId,
        handId: handId,
        timestamp: annotationData.timestamp,
        cameraImageWidth: annotationData.cameraImageWidth,
        cameraImageHeight: annotationData.cameraImageHeight,
        pinCoordinates: annotationData.pinCoordinates,
        cameraId: parseInt(annotationData.cameraId),
        calibrationId: calibration.id,
        gondolaId: annotationData.gondolaId,
        gondolaName: annotationData.gondolaName,
        planogramId: planogram.id,
        notes: note,
      })
    );

    dispatch(setSelectedEventById(customId));
    dispatch(setHighlightedTimelineEventId(customId));
    addTimelineJourneyProximityEvent({
      id: customId,
      type: ANNOTATION_TYPES.PROXIMITY,
      journeyId: annotationData.journeyId,
      timestamp: annotationData?.timestamp,
    });
    player.seek(annotationData?.timestamp, annotationData.cameraId);
    dispatch(setCurrentCamera(annotationData.cameraId));

    cameraData.setCameraModeFromCameraId(annotationData.cameraId);

    dispatch(setAnnotationData(null));
  };

  useTrackedHotkeys("esc", (e) => {
    e.preventDefault();
    handleCancel();
  });

  useTrackedHotkeys("mod + enter", (e) => {
    e.preventDefault();
    handleApply();
  });

  if (!annotationData) {
    return null;
  }

  const handleCancel = () => {
    dispatch(setAnnotationData(null));
  };

  const jumpToAnnotationTimePoint = () => {
    const gondolaName = cameraData?.findCameraGondolaName?.(
      annotationData.cameraId
    );

    player.seek(annotationData?.timestamp, annotationData.cameraId);

    dispatch(setGondolaByName(gondolaName || null));
    dispatch(setGondolaDropdownValueByName(gondolaName || null));
    dispatch(setCurrentCamera(annotationData.cameraId));

    cameraData.setCameraModeFromCameraId(annotationData.cameraId);
  };

  const pinCoordinates = formatAbsoluteCoordinates(
    annotationData?.pinCoordinates.relativeX,
    annotationData?.pinCoordinates.relativeY,
    annotationData.cameraImageWidth,
    annotationData.cameraImageHeight
  );

  return (
    <AnnotationFormWrapper>
      <InfoItem label="Event type" value="Proximity Annotation" />
      <InfoItem
        label="Journey ID"
        copyValue={annotationData?.journeyId}
        value={displayUUID(annotationData?.journeyId)}
      />
      <InfoItem
        label="Order ID"
        value={displayUUID(annotationData.orderId)}
        copyValue={annotationData.orderId}
      />
      <InfoItem label="Timestamp" value={annotationData?.timestamp}>
        <IconButton variant="ghost" onClick={jumpToAnnotationTimePoint}>
          <TargetIcon />
        </IconButton>
      </InfoItem>
      <InfoItem label="Hand" value=" " />
      <Flex>
        <SegmentedControl.Root
          value={handId}
          onValueChange={(value: HandId) => setHandId(value)}
          variant="surface"
          size="2"
          className="flex-1 my-1"
        >
          <SegmentedControl.Item value={HAND_IDS.LEFT}>
            {HAND_SIDES.LEFT} ({HAND_IDS.LEFT})
          </SegmentedControl.Item>
          <SegmentedControl.Item value={HAND_IDS.RIGHT}>
            {HAND_SIDES.RIGHT} ({HAND_IDS.RIGHT})
          </SegmentedControl.Item>
        </SegmentedControl.Root>
      </Flex>
      <InfoItem label="Pin coordinates (x,y)" value={pinCoordinates} />
      <InfoItem
        label="Camera image dimensions"
        value={`${annotationData?.cameraImageWidth}x${annotationData?.cameraImageHeight}`}
      />
      <InfoItem label="Annotated camera" value={annotationData?.cameraId} />
      <InfoItem label="Calibration ID" value={annotationData?.calibrationId} />
      <InfoItem label="Gondola name" value={annotationData?.gondolaName} />
      <InfoItem label="Gondola ID" value={annotationData?.gondolaId} />
      <InfoItem label="Planogram ID" value={annotationData?.planogramId} />

      {isEditable ? (
        <>
          <Text size="1" color="gray" className="min-w-fit">
            Note:
          </Text>
          <TextField.Root
            size="1"
            mx="1"
            placeholder="Some kind of note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </>
      ) : (
        <InfoItem label="Note" value={note} />
      )}

      <Flex justify="between" mt="8">
        <Button
          size="1"
          color="gray"
          variant="soft"
          type="button"
          onClick={handleCancel}
        >
          Cancel (esc)
        </Button>
        <Button
          size="1"
          color="gray"
          highContrast
          type="submit"
          onClick={handleApply}
        >
          Apply (ctrl + enter)
        </Button>
      </Flex>
    </AnnotationFormWrapper>
  );
};

export default ProximityAnnotationForm;
